import { CssBaseline } from '@material-ui/core';
import type { PropsWithChildren } from 'react';
import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { ThemeBlueprint } from '@backstage/plugin-app-react';
import {
  UnifiedThemeProvider,
  createUnifiedTheme,
  palettes,
} from '@backstage/theme';

const pageTheme = {
  colors: ['#080b10'],
  shape: 'none',
  backgroundImage: 'none',
  fontColor: '#edf2f7',
};

export const spatialwalkColors = {
  canvas: '#0c1118',
  paper: '#151c26',
  navigation: '#080b10',
  primary: '#6ea8ff',
  // Configuration documents sit inside a card, so they need to read as a
  // surface of their own rather than borrowing the page background, which is
  // almost the same shade. Editing darkens it further, so the difference
  // between reading and writing is visible without looking for the button.
  document: '#243044',
  documentEditing: '#070a10',
} as const;

export const spatialwalkTheme = createUnifiedTheme({
  palette: {
    ...palettes.dark,
    primary: { main: spatialwalkColors.primary, dark: '#315481' },
    background: {
      default: spatialwalkColors.canvas,
      paper: spatialwalkColors.paper,
    },
    text: { primary: '#edf2f7', secondary: '#9eabbb' },
    border: '#2b3746',
    link: '#6ea8ff',
    linkHover: '#8bb9ff',
    navigation: {
      background: spatialwalkColors.navigation,
      indicator: '#6ea8ff',
      color: '#9eabbb',
      selectedColor: '#edf2f7',
      navItem: { hoverBackground: '#151c26' },
      submenu: { background: '#0c1118' },
    },
    tabbar: { indicator: '#6ea8ff' },
  },
  defaultPageTheme: 'home',
  pageTheme: {
    home: pageTheme,
    documentation: pageTheme,
    tool: pageTheme,
    service: pageTheme,
    website: pageTheme,
    library: pageTheme,
    other: pageTheme,
    app: pageTheme,
    apis: pageTheme,
    card: pageTheme,
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { backgroundColor: '#151c26' },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottomColor: '#2b3746' },
        head: { color: '#9eabbb' },
      },
    },
    // Card grids (the Create page's templates): auto-fit collapses the empty
    // tracks auto-fill keeps, so a handful of cards sit centered at a readable
    // width instead of hugging the left edge beside a wide blank area.
    BackstageItemCardGrid: {
      styleOverrides: {
        root: {
          gridTemplateColumns:
            'repeat(auto-fit, minmax(min(100%, 24rem), 36rem))',
          justifyContent: 'center',
        },
      },
    },
  },
});

const spatialwalkBuiThemeCss = `
body[data-theme-name='spatialwalk-dark'] {
  --bui-bg-app: ${spatialwalkColors.canvas};
  --bui-bg-neutral-1: ${spatialwalkColors.paper};
  --bui-bg-neutral-2: #182c49;
  --bui-bg-neutral-3: #2b3746;
  --bui-bg-neutral-4: #315481;
}
`;

export function SpatialwalkThemeProvider({ children }: PropsWithChildren) {
  return (
    <UnifiedThemeProvider theme={spatialwalkTheme} themeName="spatialwalk-dark">
      <CssBaseline />
      <style data-spatialwalk-bui-theme>{spatialwalkBuiThemeCss}</style>
      {children}
    </UnifiedThemeProvider>
  );
}

const spatialwalkThemeExtension = ThemeBlueprint.make({
  name: 'spatialwalk-dark',
  params: {
    theme: {
      id: 'spatialwalk-dark',
      title: 'SpatialWalk Dark',
      variant: 'dark',
      Provider: SpatialwalkThemeProvider,
    },
  },
});

export const spatialwalkThemeModule = createFrontendModule({
  pluginId: 'app',
  extensions: [spatialwalkThemeExtension],
});
